"use client";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popOver";
import { cn } from "@/lib/utils";
import { useMutation } from "@apollo/client";
import {
  FacebookCounterReaction,
  FacebookSelector
} from "@charkour/react-reactions";
import { ExploreReactionsModalV2 } from "./ExploreReactionsModalV2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { gqlMessage } from "gql";
import { ICommentMessageReactionInput } from "gql/Message/mutations";
import { useExperienceGain } from "hooks/useExperienceGain";
import useTranslation from "next-translate/useTranslation";
import { useTheme } from "next-themes";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { IMessageReaction } from "types/Message.type";

const REACTION_ICON_SIZE = 16;
const SELECTOR_ICON_SIZE = 24;

interface ExploreCommentMessageReactionV2Props {
  commentMessage: string;
  reactions: IMessageReaction[];
  children: ReactNode;
}

interface CommentReactionContextValue {
  t: (key: string) => string;
  userReaction?: IMessageReaction;
  reactionCount: number;
  uniqueEmojiTypes: string[];
  hasReactions: boolean;
  selectorOpen: boolean;
  setSelectorOpen: (open: boolean) => void;
  handleLikeClick: () => void;
  handleReactionSelect: (reaction: string) => void;
  handleOpenReactions: () => void;
}

const CommentReactionContext = createContext<CommentReactionContextValue | null>(
  null
);

function useCommentReactionContext() {
  const context = useContext(CommentReactionContext);
  if (!context) {
    throw new Error(
      "Comment reaction components must be used within ExploreCommentMessageReactionV2"
    );
  }
  return context;
}

function FacebookReactionIcon({
  emoji,
  index = 1,
  ringColor,
  className = ""
}: {
  emoji: string;
  index?: number;
  ringColor: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "comment-reaction-icon inline-flex shrink-0 items-center justify-center leading-none",
        className
      )}
      style={{ width: REACTION_ICON_SIZE, height: REACTION_ICON_SIZE }}
    >
      <FacebookCounterReaction
        reaction={emoji}
        bg={ringColor}
        index={index}
        variant="facebook"
      />
    </span>
  );
}

function useCommentReactionLogic(
  commentMessage: string,
  reactions: IMessageReaction[],
  onOpenReactions: () => void
) {
  const { t } = useTranslation("common");
  const { user } = useUserStoreContext((store) => ({
    user: store.user
  }));
  const { actions: experienceActions } = useExperienceGain();

  const [localReactions, setLocalReactions] = useState<IMessageReaction[]>([]);
  const [hasEverReacted, setHasEverReacted] = useState<boolean>(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const lastServerReactions = useRef<IMessageReaction[]>([]);
  const isPendingMutation = useRef<boolean>(false);
  const lastLocalMutationAt = useRef<number>(0);

  const [messageReaction] = useMutation<
    { commentMessageReaction: IMessageReaction[] },
    {
      commentMessageReactionInput: ICommentMessageReactionInput;
    }
  >(gqlMessage.mutations.COMMENT_MESSAGE_REACTION, {
    onCompleted: ({ commentMessageReaction }) => {
      setLocalReactions(commentMessageReaction);
      lastServerReactions.current = commentMessageReaction;
      lastLocalMutationAt.current = Date.now();
      isPendingMutation.current = false;

      const userHasReactionNow = commentMessageReaction?.some(
        (r) => r.user === user?.id
      );

      if (!hasEverReacted && userHasReactionNow) {
        setHasEverReacted(true);
        experienceActions.likeMessage();
      }
    },
    onError: () => {
      setLocalReactions(lastServerReactions.current);
      isPendingMutation.current = false;
    }
  });

  useEffect(() => {
    const incomingReactions = reactions ?? [];
    const isRecentlyMutated = Date.now() - lastLocalMutationAt.current < 3500;
    const incomingDiffersFromLastMutation =
      JSON.stringify(incomingReactions) !== JSON.stringify(lastServerReactions.current);

    // Evita que una rehidratación/refetch del feed con datos stale borre la
    // reacción optimista recién aplicada. El servidor ya la persiste; si el
    // feed llega tarde con la versión anterior, conservamos el estado local.
    if (
      !isPendingMutation.current &&
      !(isRecentlyMutated && incomingDiffersFromLastMutation) &&
      incomingDiffersFromLastMutation
    ) {
      setLocalReactions(incomingReactions);
      lastServerReactions.current = incomingReactions;
    }

    if (!hasEverReacted && incomingReactions.some((r) => r.user === user?.id)) {
      setHasEverReacted(true);
    }
  }, [reactions, user?.id, hasEverReacted, localReactions]);

  const closeSelector = () => {
    setSelectorOpen(false);
  };

  const submitReaction = (reaction: string) => {
    isPendingMutation.current = true;
    lastLocalMutationAt.current = Date.now();

    const newReactions = [...localReactions];
    const existingReactionIndex = newReactions.findIndex(
      (r) => r.user === user?.id
    );

    if (existingReactionIndex >= 0) {
      newReactions[existingReactionIndex] = {
        ...newReactions[existingReactionIndex],
        emoji: reaction as IMessageReaction["emoji"]
      };
    } else {
      newReactions.push({
        user: user?.id || "",
        emoji: reaction as IMessageReaction["emoji"]
      });
    }

    setLocalReactions(newReactions);

    messageReaction({
      variables: {
        commentMessageReactionInput: {
          emoji: reaction,
          commentMessage
        }
      }
    });
    closeSelector();
  };

  const userReaction = localReactions?.find((r) => r.user === user?.id);

  const handleLikeClick = () => {
    if (!userReaction) return;

    isPendingMutation.current = true;
    lastLocalMutationAt.current = Date.now();
    const updated = localReactions.filter((r) => r.user !== user?.id);
    setLocalReactions(updated);
    messageReaction({
      variables: {
        commentMessageReactionInput: {
          emoji: "none",
          commentMessage
        }
      }
    });
  };

  const uniqueEmojiTypes = [
    ...new Set(localReactions.map((r) => r.emoji))
  ].slice(0, 3);

  return {
    t,
    userReaction,
    reactionCount: localReactions.length,
    uniqueEmojiTypes,
    hasReactions: uniqueEmojiTypes.length > 0,
    selectorOpen,
    setSelectorOpen,
    handleLikeClick,
    handleReactionSelect: submitReaction,
    handleOpenReactions: onOpenReactions
  };
}

export function ExploreCommentMessageReactionV2({
  commentMessage,
  reactions,
  children
}: ExploreCommentMessageReactionV2Props) {
  const [reactionsOpen, setReactionsOpen] = useState(false);

  const value = useCommentReactionLogic(commentMessage, reactions, () =>
    setReactionsOpen(true)
  );

  return (
    <CommentReactionContext.Provider value={value}>
      <div className="flex flex-col leading-none">{children}</div>

      <ExploreReactionsModalV2
        open={reactionsOpen}
        onClose={() => setReactionsOpen(false)}
        id={commentMessage}
        type="comment"
      />

      <style jsx global>{`
        .comment-reaction-icon > div {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: ${REACTION_ICON_SIZE}px !important;
          height: ${REACTION_ICON_SIZE}px !important;
          border-radius: 50% !important;
          line-height: 1 !important;
        }

        .comment-reaction-popover {
          transform-origin: bottom left;
        }

        .comment-reaction-popover > div {
          box-shadow:
            0 0 0 1px rgba(15, 23, 42, 0.06),
            0 4px 14px -4px rgba(15, 23, 42, 0.18) !important;
        }
      `}</style>
    </CommentReactionContext.Provider>
  );
}

export function ExploreCommentReactionCountV2() {
  const {
    reactionCount,
    uniqueEmojiTypes,
    hasReactions,
    handleOpenReactions
  } = useCommentReactionContext();
  const { theme } = useTheme();
  const ringColor = theme === "dark" ? "#27272a" : "#f1f5f9";

  if (!hasReactions) return null;

  return (
    <button
      type="button"
      onClick={handleOpenReactions}
      className="comment-reaction-count pointer-events-auto absolute bottom-0 right-2 z-10 inline-flex translate-y-1/2 cursor-pointer items-center gap-0.5 border-0 bg-transparent p-0 leading-none transition-opacity hover:opacity-80"
      aria-label={`${reactionCount} reacciones`}
    >
      <span className="text-[10px] font-normal text-slate-600 dark:text-zinc-400">
        {reactionCount}
      </span>
      <span className="flex items-center">
        {uniqueEmojiTypes.map((emoji, index) => (
          <span
            key={emoji}
            className={cn("relative inline-flex", index > 0 && "-ml-[4px]")}
            style={{ zIndex: index + 1 }}
          >
            <FacebookReactionIcon
              emoji={emoji}
              index={index + 1}
              ringColor={ringColor}
            />
          </span>
        ))}
      </span>
    </button>
  );
}

export function ExploreCommentLikeButtonV2({ className }: { className?: string }) {
  const {
    t,
    userReaction,
    selectorOpen,
    setSelectorOpen,
    handleLikeClick,
    handleReactionSelect
  } = useCommentReactionContext();

  return (
    <div className={cn("mt-px", className)}>
      <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={handleLikeClick}
            className={cn(
              "cursor-pointer border-0 bg-transparent p-0 text-[10px] font-semibold leading-none transition-colors",
              userReaction
                ? "text-[#385898] dark:text-indigo-400"
                : "text-slate-600 hover:underline dark:text-zinc-400"
            )}
          >
            {t("Me gusta")}
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="comment-reaction-popover w-auto rounded-full border-0 bg-transparent p-0 shadow-none"
          side="top"
          align="start"
          sideOffset={4}
          avoidCollisions
          collisionPadding={8}
        >
          <FacebookSelector
            iconSize={SELECTOR_ICON_SIZE}
            onSelect={handleReactionSelect}
            variant="facebook"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
